import { HttpClient } from '../http-client';
import {
  Company,
  DocumentAgentResponse,
  CreateCompanyPageResponse,
  CreateCompanyPageFromAgentPayload,
  AIContextsResponse,
  AIContextsPayload,
  DocumentAgentPayload,
  DocumentationAgentsResponse,
  DocumentationAgent,
} from './types';

class DocumentAgentAPIClient extends HttpClient {
  public getAllCompanies = async (): Promise<Company[]> => {
    try {
      const response = (await this.get(
        '/companies/searchlightV1?sort=company*companyId.desc&offset=0&limit=100',
      )) as any;

      return response?.data as Company[];
    } catch (error) {
      throw new Error('Failed to get all companies', { cause: error });
    }
  };

  public createCompanyPage = async (
    companyId: number,
    templateId: number,
  ): Promise<CreateCompanyPageResponse> => {
    try {
      const response = await this.post(
        `/document-agent/create-company-page?companyId=${companyId}&templateId=${templateId}`,
      );

      return response as CreateCompanyPageResponse;
    } catch (error) {
      throw new Error('Failed to create company page', { cause: error });
    }
  };

  public getDocumentationAgent = async (
    companyId: number,
    payload: DocumentAgentPayload,
  ): Promise<DocumentAgentResponse> => {
    try {
      const response = await this.post(
        `/agents/documentation_agents/test-document-agent?companyId=${companyId}`,
        payload,
      );

      return response as DocumentAgentResponse;
    } catch (error) {
      throw new Error('Failed to get documentation agent', { cause: error });
    }
  };

  public getAdditionalAIContexts = async (
    documentAgentId: string,
  ): Promise<AIContextsResponse> => {
    try {
      const response = await this.get(
        `/agents/documentation_agents/additional-ai-contexts/${documentAgentId}`,
      );

      return response as AIContextsResponse;
    } catch (error) {
      throw new Error('Failed to get additional AI contexts', {
        cause: error,
      });
    }
  };

  public createAdditionalAIContexts = async (
    documentAgentId: string,
    payload: AIContextsPayload,
  ): Promise<AIContextsResponse> => {
    try {
      const response = await this.post(
        `/agents/documentation_agents/additional-ai-contexts/${documentAgentId}`,
        payload,
      );

      return response as AIContextsResponse;
    } catch (error) {
      throw new Error('Failed to create additional AI contexts', {
        cause: error,
      });
    }
  };

  public updateAdditionalAIContexts = async (
    documentAgentId: string,
    payload: AIContextsPayload,
  ): Promise<AIContextsResponse> => {
    try {
      const response = await this.put(
        `/agents/documentation_agents/additional-ai-contexts/${documentAgentId}`,
        payload,
      );

      return response as AIContextsResponse;
    } catch (error) {
      throw new Error('Failed to update additional AI contexts', {
        cause: error,
      });
    }
  };

  public deleteAdditionalAIContext = async (
    documentAgentId: string,
    templateSectionId: string,
  ): Promise<void> => {
    try {
      await this.delete(
        `/agents/documentation_agents/additional-ai-contexts/${documentAgentId}?templateSectionIdsToBeDeleted=${templateSectionId}`,
      );
    } catch (error) {
      throw new Error('Failed to delete additional AI context', {
        cause: error,
      });
    }
  };

  public deleteQuestionAIContext = async (
    documentAgentId: string,
    templateSectionId: string,
    nodeId: string,
  ): Promise<void> => {
    try {
      await this.delete(
        `/agents/documentation_agents/additional-ai-contexts/${documentAgentId}/${templateSectionId}?idsToBeDeleted=${nodeId}`,
      );
    } catch (error) {
      throw new Error('Failed to delete question AI context', {
        cause: error,
      });
    }
  };

  public updateQuestionAIContext = async (
    documentAgentId: string,
    templateSectionId: string,
    nodeId: string,
    payload: AIContextsPayload,
  ): Promise<AIContextsResponse> => {
    try {
      const response = await this.put(
        `/agents/documentation_agents/additional-ai-contexts/${documentAgentId}/${templateSectionId}`,
        payload,
      );

      return response as AIContextsResponse;
    } catch (error) {
      throw new Error('Failed to update question AI context', {
        cause: error,
      });
    }
  };

  /**
   * Get documentation agents with pagination
   * GET /agents/documentation_agents?limit={limit}&offset={offset}&onlyData={onlyData}
   */
  public getDocumentationAgents = async (
    limit?: number,
    offset?: number,
    onlyData?: boolean,
  ): Promise<DocumentationAgentsResponse> => {
    try {
      const params = new URLSearchParams();

      if (limit !== undefined) {
        params.append('limit', limit.toString());
      }
      if (offset !== undefined) {
        params.append('offset', offset.toString());
      }
      if (onlyData !== undefined) {
        params.append('onlyData', onlyData.toString());
      }

      const url = `/agents/documentation_agents${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await this.get<DocumentationAgentsResponse>(url);

      return response;
    } catch (error) {
      throw new Error('Failed to get documentation agents', { cause: error });
    }
  };

  /**
   * Get a single documentation agent by ID
   * GET /agents/documentation_agents/{id}
   */
  public getDocumentationAgentById = async (
    documentAgentId: number,
  ): Promise<DocumentationAgent> => {
    try {
      const url = `/agents/documentation_agents/${documentAgentId}`;
      const response = await this.get<DocumentationAgent>(url);

      return response;
    } catch (error) {
      throw new Error('Failed to get documentation agent by ID', {
        cause: error,
      });
    }
  };

  /**
   * Update a documentation agent (send entire object)
   * PUT /agents/documentation_agents/{id}
   */
  public updateDocumentationAgent = async (
    payload: DocumentationAgent,
  ): Promise<DocumentationAgent> => {
    try {
      const url = `/agents/documentation_agents/${payload.id}`;
      const response = await this.put<DocumentationAgent, DocumentationAgent>(
        url,
        payload,
      );

      return response;
    } catch (error) {
      throw new Error('Failed to update documentation agent', {
        cause: error,
      });
    }
  };

  /**
   * Create company page from documentation agent
   * POST /agents/documentation_agents/invoke?companyId={companyId}&documentAgentId={documentAgentId}&confirmed={confirmed}
   */
  public createCompanyPageFromAgent = async (
    companyId: number,
    documentAgentId: number,
    payload?: CreateCompanyPageFromAgentPayload,
    confirmed?: boolean,
  ): Promise<CreateCompanyPageResponse> => {
    try {
      let url = `/agents/documentation_agents/invoke?companyId=${companyId}&documentAgentId=${documentAgentId}`;

      if (confirmed !== undefined) {
        url += `&confirmed=${confirmed}`;
      }

      // Strip confirmed from payload; it is sent as query param only
      const payloadWithoutConfirmed =
        payload && 'confirmed' in payload
          ? (() => {
              const { confirmed: _c, ...rest } = payload;

              return rest as CreateCompanyPageFromAgentPayload;
            })()
          : payload;

      // If files are present, send as FormData similar to knowledge-base API
      if (
        payloadWithoutConfirmed?.files &&
        payloadWithoutConfirmed.files.length > 0
      ) {
        const formData = new FormData();

        payloadWithoutConfirmed.files.forEach((file) => {
          formData.append('file', file);
        });

        const { files, ...payloadMeta } = payloadWithoutConfirmed;
        const jsonString = JSON.stringify(payloadMeta);
        const requestBlob = new Blob([jsonString], {
          type: 'application/json',
        });

        formData.append('request', requestBlob);

        return this.post<CreateCompanyPageResponse, FormData>(url, formData);
      }

      const response = (
        payloadWithoutConfirmed
          ? await this.post(url, payloadWithoutConfirmed)
          : await this.post(url)
      ) as CreateCompanyPageResponse;

      return response;
    } catch (error: any) {
      const responseData = error?.response?.data;

      throw new Error('Failed to create company page from agent', {
        cause: responseData ?? error,
      });
    }
  };

  /**
   * Update company page sources
   * PUT /agents/documentation_agents/update-company-page?documentId={documentId}
   */
  public updateCompanyPageSources = async (
    documentId: number,
    payload: {
      EMAIL?: number[];
      MEETING?: number[];
    },
  ): Promise<void> => {
    try {
      const url = `/agents/documentation_agents/update-company-page?documentId=${documentId}`;

      await this.put(url, payload);
    } catch (error) {
      throw new Error('Failed to update company page sources', {
        cause: error,
      });
    }
  };
}

export const DocumentAgentAPI = new DocumentAgentAPIClient();
